import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Input, Button, Spin, message, Statistic, Tag, Alert } from 'antd';
import { MailOutlined, LockOutlined, FileTextOutlined, DollarOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import '../styles/bulkPaymentApproval.css';
import logo from "../images/logo_symbol.png";
import { useDispatch } from 'react-redux';
import { appSelector } from '../helpers/appSelector';
import { bulkPaymentCheckRequest, approveBulkPaymentRequest, rejectBulkPaymentRequest, clearBulkPaymentStates, bulkPaymentExportRequest } from '../store/bulk-payment';
import { BulkPaymentState } from '../store/bulk-payment/types';
import CustomTable from '../components/bulkPayment/CustomTable';
import moment from 'moment';

const translations = {
  en: {
    title: "Bulk Payment Approval",
    enterEmailOtp: "Please enter your email and OTP to view the payment details",
    enterEmail: "Please enter your email",
    validEmail: "Please enter a valid email",
    emailPlaceholder: "Enter your email",
    enterOtp: "Please enter the OTP",
    otpLength: "OTP must be 5 digits",
    otpPlaceholder: "Enter OTP",
    checking: "Checking...",
    checkPayment: "Check Payment",
    detailsTitle: "Bulk Payment Details",
    detailsDesc: "Below are the payment details for this file",
    totalAmount: "Total Amount ({{currency}})",
    totalTransactions: "Total Transactions",
    transactions: "Transactions",
    approving: "Approving...",
    approveBulkPayment: "Approve Bulk Payment",
    msisdn: "MSISDN",
    provider: "Provider",
    currency: "Currency",
    amount: "Amount",
    paymentDate: "Payment Date",
    status: "Status",
    pending: "Pending",
    approved: "Approved",
    channel: "Channel",
    failed: "Failed",
    paymentAlreadyApproved: "Payment Already Approved",
    paymentAlreadyApprovedDesc: "This bulk payment has already been approved.",
    paymentProcessing: "Approval received. Transactions are being processed.",
    rejectionProcessing: "Rejection received. Transactions are being processed.",
    rejectBulkPayment: "Reject Bulk Payment",
    duplicateMsisdn: "Duplicate MSISDN"
  },
  fr: {
    title: "Approbation de paiement groupé",
    enterEmailOtp: "Veuillez saisir votre e-mail et OTP pour voir les détails du paiement",
    enterEmail: "Veuillez saisir votre e-mail",
    validEmail: "Veuillez saisir un e-mail valide",
    emailPlaceholder: "Entrez votre e-mail",
    enterOtp: "Veuillez saisir le code OTP",
    otpLength: "Le code OTP doit comporter 5 chiffres",
    otpPlaceholder: "Entrez le code OTP",
    checking: "Vérification...",
    checkPayment: "Vérifier le paiement",
    detailsTitle: "Détails du paiement groupé",
    detailsDesc: "Ci-dessous les détails de paiement pour ce fichier",
    totalAmount: "Montant total ({{currency}})",
    totalTransactions: "Nombre total de transactions",
    transactions: "Transactions",
    approving: "Approbation...",
    approveBulkPayment: "Approuver le paiement groupé",
    rejectBulkPayment: "Rejeter le paiement groupé",
    duplicateMsisdn: "MSISDN Dupliqué",
    msisdn: "MSISDN",
    provider: "Fournisseur",
    currency: "Devise",
    amount: "Montant",
    paymentDate: "Date de paiement",
    status: "Statut",
    pending: "En attente",
    approved: "Approuvé",
    channel: "Canal",
    failed: "Échoué",
    paymentAlreadyApproved: "Paiement Déjà Approuvé",
    paymentAlreadyApprovedDesc: "Ce paiement groupé a déjà été approuvé.",
    paymentProcessing: "Approbation reçue. Les transactions sont en cours de traitement.",
    rejectionProcessing: "Rejet reçu. Les transactions sont en cours de traitement."
  }
};

const BulkPaymentApproval: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { 
    checkResult, 
    checkLoading, 
    checkError, 
    isExporting, 
    exportError,
    approvalError,
    rejectError,
    approvalSuccess,
    rejectSuccess
  } = appSelector((state: { bulkPayment: BulkPaymentState }) => state.bulkPayment);
  const [form] = Form.useForm();
  const [verified, setVerified] = useState(false);
  const [checker, setChecker] = useState<{ email: string; otp: string } | null>(null);
  const [lang, setLang] = useState<'en' | 'fr'>('en');
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [approvalProcessing, setApprovalProcessing] = useState(false);
  const [rejectionProcessing, setRejectionProcessing] = useState(false);
  
  const [approveButtonClicked, setApproveButtonClicked] = useState(false);
  const [rejectButtonClicked, setRejectButtonClicked] = useState(false);

  // Get fileId from URL query params
  const params = new URLSearchParams(location.search);
  const fileId = parseInt(params.get('fileId') || '0', 10);

  // Local t function
  const t = (key: keyof typeof translations.en, vars?: Record<string, string | number>) => {
    let str = translations[lang][key] || key;
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        str = str.replace(`{{${k}}}`, String(v));
      });
    }
    return str;
  };

  useEffect(() => {
    if (fileId > 0) {
      form.setFieldsValue({ fileId });
    }
  }, [fileId, form]);

  useEffect(() => {
    if (checkResult && checkResult.length > 0) {
      setVerified(true);
      setShowLoginForm(false);
      
      const isRejected = checkResult.some(item => item.statusId === 4); 
      const isApproved = checkResult.some(item => item.statusId === 3); 

      if (isRejected) {
        setRejectionProcessing(true);
      } else if (isApproved) {
        setApprovalProcessing(true);
      } else {
        const hasNonPendingTransactions = checkResult.some(item => item.statusId !== 2);
        if (hasNonPendingTransactions) {
          setApprovalProcessing(true);
        }
      }
    }
  }, [checkResult]);

  useEffect(() => {
    if (checkError) {
      message.error(checkError.message || 'Failed to fetch payment details');
      setShowLoginForm(true);
    }
  }, [checkError]);

  useEffect(() => {
    if (exportError) {
      message.error(exportError);
    }
  }, [exportError]);

  useEffect(() => {
    if (approvalError) {
      setApprovalProcessing(false);
      setApproveButtonClicked(false);
      message.error(approvalError.message || approvalError);
      dispatch(clearBulkPaymentStates());
    }
    if (rejectError) {
      setRejectionProcessing(false);
      setRejectButtonClicked(false);
      message.error(rejectError.message || rejectError);
      dispatch(clearBulkPaymentStates());
    }
    if (approvalSuccess || rejectSuccess) {
      dispatch(clearBulkPaymentStates());
    }
  }, [approvalError, rejectError, approvalSuccess, rejectSuccess, dispatch]);

  const handleSubmit = async (values: any) => {
    if (fileId <= 0) {
      message.error('Invalid file ID');
      return;
    }
    setChecker({ email: values.email, otp: values.otp });
    dispatch(bulkPaymentCheckRequest({
      fileId,
      email: values.email,
      otp: values.otp
    }));
  };

  const handleReject = () => {
    if (rejectButtonClicked || !checker) {
      if (!checker) {
        setShowLoginForm(true);
      }
      return;
    }
    
    setRejectButtonClicked(true);

    dispatch(rejectBulkPaymentRequest({
      fileId,
      email: checker.email,
      otp: checker.otp
    }));

    setTimeout(() => {
      setRejectionProcessing(true);
      message.success(t('rejectionProcessing'), 10);

      dispatch(bulkPaymentCheckRequest({
        fileId,
        email: checker.email,
        otp: checker.otp
      }));
    }, 3000);
  };

  const handleApprove = () => {
    if (approveButtonClicked || !checker) {
      if (!checker) {
        setShowLoginForm(true);
      }
      return;
    }
    
    setApproveButtonClicked(true); 

    dispatch(approveBulkPaymentRequest({
      fileId,
      email: checker.email,
      otp: checker.otp
    }));

    setTimeout(() => {
      setApprovalProcessing(true);
      message.success(t('paymentProcessing'), 10);

      dispatch(bulkPaymentCheckRequest({
        fileId,
        email: checker.email,
        otp: checker.otp
      }));
    }, 3000);
  };

  const handleExport = () => {
    if (!checker) {
      setShowLoginForm(true);
      return;
    }
    dispatch(bulkPaymentExportRequest({
      fileId,
      email: checker.email,
      otp: checker.otp
    }));
  };

  const totalsByCurrency: Record<string, number> = {};
  let totalTransactions = 0;
  let duplicateMsisdnCount = 0;

  if (checkResult && checkResult.length > 0) {
    const msisdnCounts: Record<string, number> = {};
    checkResult.forEach(item => {
      if (!totalsByCurrency[item.currency]) totalsByCurrency[item.currency] = 0;
      totalsByCurrency[item.currency] += item.amount;
      totalTransactions += 1;
      if (item.msisdn) {
        msisdnCounts[item.msisdn] = (msisdnCounts[item.msisdn] || 0) + 1;
      }
    });

    Object.values(msisdnCounts).forEach(count => {
      if (count > 1) {
        duplicateMsisdnCount += 1;
      }
    });
  }

  const calculateTransactionsByChannel = (data: any[]) => {
    const channelCounts: { [key: string]: number } = {};
    data.forEach(item => {
      const channel = item.channel || 'Unknown';
      if (!channelCounts[channel]) {
        channelCounts[channel] = 0;
      }
      channelCounts[channel] += 1;
    });
    return channelCounts;
  };

  const transactionsByChannel = React.useMemo(() => {
    return checkResult && checkResult.length > 0
      ? calculateTransactionsByChannel(checkResult)
      : {};
  }, [checkResult]);

  const allTransactionsApproved = checkResult && checkResult.length > 0 &&
    checkResult.every(item => item.statusId === 1);

  const columns = [
    {
      title: t('msisdn'),
      dataIndex: 'msisdn',
      key: 'msisdn',
      align: 'center' as const,
      render: (text: string) => <span style={{ color: '#35b9e6' }}>{text}</span>,
    },
    {
      title: t('channel'),
      dataIndex: 'channel',
      key: 'channel',
      align: 'center' as const,
      render: (text: string | null) => text || '-',
    },
    {
      title: t('currency'),
      dataIndex: 'currency',
      key: 'currency',
      align: 'center' as const,
    },
    {
      title: t('amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'center' as const,
      render: (amount: number) => amount?.toFixed(2) || '0.00',
    }
  ];

  const processedData = React.useMemo(() => {
    if (!checkResult || !Array.isArray(checkResult)) {
      return [];
    }
    return checkResult.map((item, index) => {
      const uniqueKey = item.id
        ? `${item.id}`
        : `${item.msisdn || 'unknown'}-${item.amount || 0}-${index}`;
      return {
        ...item,
        key: uniqueKey,
        msisdn: item.msisdn || '',
        providerName: item.providerName || null,
        currency: item.currency || '',
        amount: item.amount || 0,
        paymentDate: item.paymentDate || '',
        statusId: item.statusId || 0,
      };
    });
  }, [checkResult]);

  useEffect(() => {
    if (showLoginForm) {
      form.resetFields();
    }
  }, [showLoginForm, form]);

  const renderLoginForm = () => (
    <div className="approval-card">
      <div className="approval-header">
        <h2>{t('title')}</h2>
        <p>{t('enterEmailOtp')}</p>
      </div>
      <div className="approval-form">
        <Form form={form} onFinish={handleSubmit}>
          <Form.Item
            name="email"
            className="animate-fadeInUp delay-1"
            rules={[
              { required: true, message: t('enterEmail') },
              { type: 'email', message: t('validEmail') }
            ]}
          >
            <div className="input-with-icon">
              <Input placeholder={t('emailPlaceholder')} size="large" />
              <MailOutlined />
            </div>
          </Form.Item>
          <Form.Item
            name="otp"
            className="animate-fadeInUp delay-2"
            rules={[
              { required: true, message: t('enterOtp') },
              { len: 5, message: t('otpLength') }
            ]}
          >
            <div className="input-with-icon">
              <Input placeholder={t('otpPlaceholder')} size="large" />
              <LockOutlined />
            </div>
          </Form.Item>
          <Form.Item className="animate-fadeInUp delay-3">
            <Button
              type="primary"
              htmlType="submit"
              className="approval-btn"
              block
              loading={checkLoading}
            >
              {checkLoading ? t('checking') : t('checkPayment')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );

  return (
    <div className="approval-container">
      <img src={logo} alt="Company Logo" className="approval-logo" />

      <div style={{ position: 'absolute', top: 40, right: 60, zIndex: 2 }}>
        <Button
          type={lang === 'en' ? 'primary' : 'default'}
          size="small"
          style={{ marginRight: 8 }}
          onClick={() => setLang('en')}
        >
          EN
        </Button>
        <Button
          type={lang === 'fr' ? 'primary' : 'default'}
          size="small"
          onClick={() => setLang('fr')}
        >
          FR
        </Button>
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Show login form if not verified OR checker details are not available */}
        {(!verified || !checker) ? (
          renderLoginForm()
        ) : (
          <>
            {/* Only show alert when user is logged in and verified */}
            {allTransactionsApproved && (
              <Alert
                message={t('paymentAlreadyApproved')}
                description={t('paymentAlreadyApprovedDesc')}
                type="success"
                showIcon
                style={{ marginTop: 16, marginLeft: 32, marginRight: 122 }}
              />
            )}
            <div className="approval-header">
              <h2>{t('detailsTitle')}</h2>
            </div>
            <div className="approval-form">
              <Spin spinning={checkLoading}>
                <div className="file-details animate-fadeInUp">
                  <Row gutter={16} className="stats-summary-row" justify="center" style={{ display: 'flex', justifyContent: 'center', textAlign: 'center', marginBottom: 32 }}>
                    {Object.entries(totalsByCurrency).map(([currency, amount]) => (
                      <Col key={currency} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Card className="stat-card amount-card">
                          <div className="stat-icon">
                            {/* <DollarOutlined /> */}
                          </div>
                          <Statistic
                            title={<span className="stat-title">{t('totalAmount', { currency })}</span>}
                            value={amount}
                            precision={2}
                            prefix={currency}
                            className="stat-value"
                          />
                          <div className="stat-background"></div>
                        </Card>
                      </Col>
                    ))}
                    <Col style={{ display: 'flex', justifyContent: 'center' }}>
                      <Card className="stat-card transactions-card">
                        <div className="stat-icon">
                          {/* <FileTextOutlined /> */}
                        </div>
                        <Statistic
                          title={<span className="stat-title">{t('totalTransactions')}</span>}
                          value={totalTransactions}
                          className="stat-value"
                        />
                        <div className="stat-background"></div>
                      </Card>
                    </Col>
                    <Col style={{ display: 'flex', justifyContent: 'center' }}>
                      <Card className="stat-card transactions-card">
                        <div className="stat-icon">
                          {/* <FileTextOutlined /> */}
                        </div>
                        <Statistic
                          title={<span className="stat-title">{t('duplicateMsisdn')}</span>}
                          value={duplicateMsisdnCount}
                          className="stat-value"
                        />
                        <div className="stat-background"></div>
                      </Card>
                    </Col>
                  </Row>

                  <div className="transactions-table animate-fadeInUp">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                      <Button
                        type="default"
                        onClick={handleExport}
                        loading={isExporting}
                        disabled={isExporting || !checker}
                        icon={<FileTextOutlined />}
                        style={{
                          background: 'linear-gradient(90deg, #35b9e6 0%, #4fd1c5 100%)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          fontWeight: 600,
                          boxShadow: '0 2px 8px rgba(53,185,230,0.15)',
                          padding: '0 28px',
                          height: 40,
                          fontSize: 16,
                          transition: 'background 0.2s',
                        }}
                        className="export-btn-nice"
                      >
                        {isExporting ? t('checking') : 'Export'}
                      </Button>
                    </div>
                    <CustomTable columns={columns} data={processedData} pageSize={10} />
                  </div>
                  <div style={{ marginTop: 32, textAlign: 'center' }}>
                    <Button
                      type="primary"
                      className="approval-btn"
                      disabled={
                        approveButtonClicked || 
                        approvalProcessing || 
                        rejectionProcessing || 
                        allTransactionsApproved ||
                        rejectButtonClicked
                      }
                      onClick={handleApprove}
                      htmlType="button"
                      loading={approveButtonClicked}
                      style={{
                        display: allTransactionsApproved ? 'none' : 'inline-block',
                        marginRight: 8
                      }}
                    >
                      {approveButtonClicked ? t('approving') : t('approveBulkPayment')}
                    </Button>
                    <Button
                      className="approval-btn reject-btn-style"
                      disabled={
                        rejectButtonClicked ||
                        approvalProcessing || 
                        rejectionProcessing || 
                        allTransactionsApproved ||
                        approveButtonClicked
                      }
                      onClick={handleReject}
                      htmlType="button"
                      loading={rejectButtonClicked}
                      style={{
                        display: allTransactionsApproved ? 'none' : 'inline-block'
                      }} 
                    >
                      {rejectButtonClicked ? t('checking') : t('rejectBulkPayment')}
                    </Button>
                  </div>
                </div>
              </Spin>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BulkPaymentApproval;